import { getEvisearchSuggestions } from './evisearchSuggestionsSource';
import {
  mergeSuggestionSources,
  SuggestionSource,
  SuggestionsParams,
  SuggestionsResponse,
} from './mergeSuggestionSources';
import { getPilotPresetOptions } from './pilotSuggestionsSource';

const DYNAMIC_FILTER_PREFIX = 'DYN_';

/**
 * Fetches filter suggestions with fan-out to Pilot and Evisearch.
 *
 * - Calls Pilot /preset-options for static/runtime presets (users, enums, regions)
 * - Calls Evisearch /suggestions for search-derived values
 * - Merges results with Pilot presets taking priority
 *
 * For static filters (folder, file_type, etc.) that have no Pilot field ID:
 * - Calls Evisearch only
 *
 * Graceful degradation: if either source fails, the other's results are returned.
 */
export const getFilterSuggestions = async ({
  filterId,
  type,
  query,
}: SuggestionsParams): Promise<SuggestionsResponse> => {
  const pilotId = toPilotFieldId(filterId);

  if (!pilotId) {
    return getEvisearchSuggestions(filterId, type, query);
  }

  const [pilotResponse, evisearchResponse] = await Promise.all([
    fetchWithFallback(
      () => getPilotPresetOptions(pilotId, query),
      'Pilot preset-options',
    ),
    fetchWithFallback(
      () => getEvisearchSuggestions(filterId, type, query),
      'Evisearch suggestions',
    ),
  ]);

  const sources: SuggestionSource[] = [
    { ...pilotResponse, priority: 10, returnsFullSet: true },
    { ...evisearchResponse, priority: 1 },
  ];

  return mergeSuggestionSources(sources);
};

const fetchWithFallback = async (
  fetchFn: () => Promise<SuggestionsResponse>,
  sourceName: string,
): Promise<SuggestionsResponse> => {
  try {
    return await fetchFn();
  } catch (error) {
    console.error(
      `[suggestions] ${sourceName} failed, continuing with other sources`,
      error,
    );
    return { values: [], selectAllCount: '0' };
  }
};

/**
 * Extracts the Pilot numeric field ID from an Evisearch filter ID.
 *
 * - "DYN_123" → "123"
 * - "123" → "123"
 * - "folder_Document" → null (static filter, no Pilot presets)
 */
const toPilotFieldId = (filterId: string): string | null => {
  if (filterId.startsWith(DYNAMIC_FILTER_PREFIX)) {
    return filterId.slice(DYNAMIC_FILTER_PREFIX.length);
  }
  if (/^\d+$/.test(filterId)) {
    return filterId;
  }
  return null;
};
